import { useContext, useMemo } from 'react';
import { formatNumber } from 'src/components/helper/formatNumber';
import { formatPrice } from 'src/components/helper/formatPrice';
import { BrandContext } from 'src/contexts/BrandContext';
import { HeaderContext } from 'src/contexts/HeaderContext';
import { useBrandSummaryMetrics, useSummarySNS } from 'src/queries';
import type { Numbers } from 'src/ui-components/helpers/formatters';

import type { IBlockData } from '../helpers/types';

export const useGetValues = ({
  type,
  change_field,
  field,
  decimal,
  dataUse,
  formatPrevious,
}: Pick<
  IBlockData,
  'type' | 'change_field' | 'field' | 'decimal' | 'dataUse' | 'formatPrevious'
>) => {
  const { brand } = useContext(BrandContext);
  const { data: response, isLoading: brandIsFetching } = useBrandSummaryMetrics();
  const { data: SNSResponse, isLoading: snsIsFetching } = useSummarySNS();
  const { currency, comparativeValue } = useContext(HeaderContext);

  const locale = brand?.currencyLocale ?? 'en-US';
  const data = dataUse === 'sns' ? SNSResponse?.data : response?.data?.data;
  const info = response?.data?.info;

  const previousDates = useMemo(() => {
    switch (comparativeValue.value) {
      case 'py':
        return `${info?.py?.py_s} - ${info?.py?.py_e}:`;
      case 'pc':
        return `${info?.previous_custom?.pc_s} - ${info?.previous_custom?.pc_e}:`;
      case 'pmsd':
        return `${info?.pmsd?.pmsd_s} - ${info?.pmsd?.pmsd_e}:`;
      default:
        return `${info?.pt?.pt_s} - ${info?.pt?.pt_e}:`;
    }
  }, [comparativeValue.value, info]);

  const previousValue = useMemo(() => {
    let rawValue: number;
    if (formatPrevious?.field) {
      rawValue = data?.[formatPrevious?.field];
    } else {
      switch (comparativeValue.value) {
        case 'py':
          rawValue = data?.[`py_${field}`];
          break;
        case 'pc':
          rawValue = data?.[`pc_${field}`];
          break;
        case 'pmsd':
          rawValue = data?.[`pmsd_${field}`];
          break;
        default:
          rawValue = data?.[`pt_${field}`];
          break;
      }
    }

    switch (formatPrevious?.type || type) {
      case 'percent':
        return formatNumber({
          value: rawValue,
          style: 'percent',
          ...(formatPrevious?.decimal
            ? {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            : {}),
        });
      case 'currency':
        return formatPrice({
          amount: rawValue,
          currency,
          locale,
          isRounded: !decimal,
        });
      default:
      case 'number':
        return formatNumber({ value: rawValue });
    }
  }, [formatPrevious, comparativeValue.value, type, data, field, currency, locale, decimal]);

  const {
    percentValue,
    positiveDynamics,
  }: {
    percentValue: string;
    positiveDynamics: Numbers.DynamicType;
  } = useMemo(() => {
    let rawValue: number;
    if (formatPrevious?.field) {
      rawValue = data?.[formatPrevious?.field];
    } else {
      switch (comparativeValue.value) {
        case 'py':
          rawValue = data?.[`py_previous_${change_field}`];
          break;
        case 'pc':
          rawValue = data?.[`pc_previous_${change_field}`];
          break;
        case 'pmsd':
          rawValue = data?.[`pmsd_previous_${change_field}`];
          break;
        default:
          rawValue = data?.[`previous_${change_field}`];
          break;
      }
    }

    return {
      percentValue: formatNumber({
        value: rawValue,
        style: 'percent',
        ...(decimal
          ? {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          : {}),
      }),
      positiveDynamics: !rawValue ? 'neutral' : rawValue > 0 ? 'positive' : 'negative',
    };
  }, [change_field, comparativeValue.value, data, decimal, formatPrevious]);

  const value = useMemo(() => {
    switch (type) {
      case 'percent':
        return formatNumber({
          value: data?.[field],
          style: 'percent',
          ...(decimal
            ? {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            : {}),
        });
      case 'number':
        return formatNumber({ value: data?.[field] });
      default:
      case 'currency':
        return formatPrice({
          amount: data?.[field],
          currency,
          locale,
          isRounded: !decimal,
        });
    }
  }, [type, data, field, decimal, currency, locale]);

  const customTooltip = useMemo(() => {
    switch (field) {
      case 'page_views':
        return `Hits in your Amazon.com presence for the selected time period as long as you had a valid offer" (whether you had the Buy Box or not); May be calculated as run rate: ${info?.bbx?.diff} of ${info?.pt?.diff} days used`;
      case 'sessions':
        return `"Sessions are visits to your Amazon.com pages by a user. All activity within a 24-hour period is considered a session.";
         May be calculated as run rate: ${info?.bbx?.diff} of ${info?.pt?.diff} days used.`;
      case 'bbx':
        return `This is the percentage of page views where the buy box (the add to shopping cart link) appeared on the page for customers to add your product to their cart.
         <br> <br>Recent days may have limited data: ${info?.bbx?.min} to ${info?.bbx?.max} shown.`;
      case 'wk8_units':
        return ` Estimated units to ship in the next 8 weeks, as of ${data?.latest_date ?? 'N/A'}`;
      case 'subscriptions':
        return `
          <div>Total Subscriptions as of ${data?.latest_date ?? 'N/A'}</div>
          <br />
          <div>${data?.previous_subscriptions_change} New Subscriptions since ${data?.previous_date ?? 'N/A'}</div>
          <br />
          <div># of new Subscriptions since the most recent report BEFORE the time frame</div>`;
      default:
        return '';
    }
  }, [data, field, info]);

  const isLoading = useMemo(
    () => (dataUse === 'sns' ? snsIsFetching : brandIsFetching),
    [brandIsFetching, dataUse, snsIsFetching],
  );

  return {
    value,
    previousValue,
    percentValue,
    positiveDynamics,
    previousDates,
    customTooltip,
    isLoading,
  };
};
